import { Injectable } from '@angular/core'
import { Action, Selector, State, StateContext } from '@ngxs/store'
import { tap } from 'rxjs/operators'
import { ShopsStateModel } from './shops.model'
import { ShopsService } from '../shops.service'
import {
  CopyPreferredVendors,
  EditLocationAttention,
  GetAllDistrictManagers,
  GetAllLocationGroups,
  GetAllRegionalManagers,
  GetAllShops,
  GetAllVendors,
  GetAttentionSummary,
  GetPreferredVendors,
  GetPreferredVendorsDetail,
  GetStatusBoard,
  GetVendorsList,
  GetVendorDocument,
  GetVendorsSummaryByLevel,
  UpdateLocationUsafWarehouse,
  UpdatePreferredVendors
} from './shops.actions'
import { Dictionary, groupBy, sortBy, uniq, uniqBy } from 'lodash'
import { ISelectItem } from '@lla-platform/core/core-ui'
import { IShopLocationItem } from '../../interfaces/shops.interface'

type ShopGroupKey = 'regionalManager' | 'districtManager' | 'locationGroup'

const SHOP_GROUP_FIELDS: Record<
  ShopGroupKey,
  { idKey: keyof IShopLocationItem; nameKey: keyof IShopLocationItem }
> = {
  regionalManager: { idKey: 'regionalManagerId', nameKey: 'regionalManagerName' },
  districtManager: { idKey: 'districtManagerId', nameKey: 'districtManagerName' },
  locationGroup: { idKey: 'locationGroupId', nameKey: 'locationGroupName' }
}

function shopsWithField(
  state: ShopsStateModel,
  idKey: keyof IShopLocationItem,
  onlyUserShops: boolean
): IShopLocationItem[] {
  return (state.allShops ?? []).filter(
    (el) => (!onlyUserShops || el.userHasAccess) && el.isActive && el[idKey]
  )
}

function groupItemsList(
  state: ShopsStateModel,
  groupKey: ShopGroupKey,
  onlyUserShops: boolean
): ISelectItem[] {
  const { idKey, nameKey } = SHOP_GROUP_FIELDS[groupKey]
  return sortBy(
    uniqBy(
      shopsWithField(state, idKey, onlyUserShops).map((el) => ({
        label: (el[nameKey] as string) ?? '',
        value: (el[idKey] as string) ?? ''
      })),
      (x) => x.value
    ),
    (x) => x.label
  )
}

function groupShopsBy(
  state: ShopsStateModel,
  groupKey: ShopGroupKey,
  onlyUserShops: boolean
): Dictionary<IShopLocationItem[]> {
  const { idKey } = SHOP_GROUP_FIELDS[groupKey]
  return groupBy(shopsWithField(state, idKey, onlyUserShops), (x) => x[idKey] as string)
}

@State<ShopsStateModel>({
  name: 'shops'
})
@Injectable()
export class ShopsState {
  constructor(private shopsService: ShopsService) {}

  @Selector()
  static allShops(state: ShopsStateModel) {
    return sortBy(state.allShops, (x) => x.name)
  }

  @Selector()
  static statusBoardLocations(state: ShopsStateModel) {
    return state.statusBoardLocations
  }

  @Selector()
  static attentionSummary(state: ShopsStateModel) {
    return state.attentionSummary
  }

  @Selector()
  static allShopsAsList(state: ShopsStateModel) {
    return sortBy(
      (state.allShops ?? []).filter((el) => el.isActive),
      (x) => x.name
    ).map((el) => ({
      value: el.id,
      label: el.name ?? '',
      extraInfo: el
    }))
  }

  @Selector()
  static userShopsAsList(state: ShopsStateModel): ISelectItem[] {
    return sortBy(
      (state.allShops ?? []).filter((el) => el.userHasAccess && el.isActive),
      (x) => x.name
    ).map((el) => ({ value: el.id, label: el.name ?? '', extraInfo: el }))
  }

  @Selector()
  static allStates(state: ShopsStateModel) {
    return sortBy(
      uniq(
        (state.allShops ?? [])
          .filter((el) => el.isActive)
          .map((el) => el.state ?? [])
          .flat()
      )
    )
  }

  @Selector()
  static userStates(state: ShopsStateModel) {
    return sortBy(
      uniq(
        (state.allShops ?? [])
          .filter((el) => el.userHasAccess && el.isActive)
          .map((el) => el.state ?? [])
          .flat()
      )
    )
  }

  @Selector()
  static allRegionalManagersInLocations(state: ShopsStateModel): ISelectItem[] {
    return groupItemsList(state, 'regionalManager', false)
  }

  @Selector()
  static userRegionalManagersInLocations(state: ShopsStateModel): ISelectItem[] {
    return groupItemsList(state, 'regionalManager', true)
  }

  @Selector()
  static allDistrictManagersInLocations(state: ShopsStateModel): ISelectItem[] {
    return groupItemsList(state, 'districtManager', false)
  }

  @Selector()
  static userDistrictManagersInLocations(state: ShopsStateModel): ISelectItem[] {
    return groupItemsList(state, 'districtManager', true)
  }

  @Selector()
  static allLocationGroupsInLocations(state: ShopsStateModel): ISelectItem[] {
    return groupItemsList(state, 'locationGroup', false)
  }

  @Selector()
  static userLocationGroupsInLocations(state: ShopsStateModel): ISelectItem[] {
    return groupItemsList(state, 'locationGroup', true)
  }

  @Selector()
  static allDivisions(state: ShopsStateModel) {
    return sortBy(
      uniq(
        (state.allShops ?? [])
          .filter((el) => el.isActive)
          .map((el) => el.division ?? [])
          .flat()
      )
    )
  }

  @Selector()
  static userShopsGroupByStates(state: ShopsStateModel) {
    return groupBy(
      (state.allShops ?? []).filter((el) => el.userHasAccess && el.isActive),
      (x) => x.state
    )
  }

  @Selector()
  static allShopsGroupByStates(state: ShopsStateModel) {
    return groupBy(
      (state.allShops ?? []).filter((el) => el.isActive),
      (x) => x.state
    )
  }

  @Selector()
  static userShopsGroupByRegionalManager(state: ShopsStateModel) {
    return groupShopsBy(state, 'regionalManager', true)
  }

  @Selector()
  static allShopsGroupByRegionalManager(state: ShopsStateModel) {
    return groupShopsBy(state, 'regionalManager', false)
  }

  @Selector()
  static userShopsGroupByDistrictManager(state: ShopsStateModel) {
    return groupShopsBy(state, 'districtManager', true)
  }

  @Selector()
  static allShopsGroupByDistrictManager(state: ShopsStateModel) {
    return groupShopsBy(state, 'districtManager', false)
  }

  @Selector()
  static userShopsGroupByLocationGroup(state: ShopsStateModel) {
    return groupShopsBy(state, 'locationGroup', true)
  }

  @Selector()
  static allShopsGroupByLocationGroup(state: ShopsStateModel) {
    return groupShopsBy(state, 'locationGroup', false)
  }

  @Selector()
  static singleShopUser(state: ShopsStateModel) {
    return state.singleShopUser
  }

  @Selector()
  static singleShopId(state: ShopsStateModel) {
    return state.singleShopId
  }

  @Selector()
  static allVendors(state: ShopsStateModel) {
    return state.allVendors ?? []
  }

  @Selector()
  static vendorsList(state: ShopsStateModel) {
    return state.vendorsList
  }

  @Selector()
  static vendorDocument(state: ShopsStateModel) {
    return state.vendorDocument
  }

  @Selector()
  static vendorsSummaryByLevel(state: ShopsStateModel) {
    return state.vendorsSummaryByLevel ?? []
  }

  @Selector()
  static preferredVendors(state: ShopsStateModel) {
    return state.preferredVendors ?? []
  }

  @Selector()
  static preferredVendorsDetail(state: ShopsStateModel) {
    return state.preferredVendorsDetail
  }

  @Selector()
  static regionalManagers(state: ShopsStateModel) {
    return state.regionalManagers
  }

  @Selector()
  static districtManagers(state: ShopsStateModel) {
    return state.districtManagers
  }

  @Selector()
  static locationGroups(state: ShopsStateModel) {
    return state.locationGroups
  }

  @Action(GetAllShops)
  getAllShops(ctx: StateContext<ShopsStateModel>) {
    ctx.patchState({
      allShops: undefined
    })
    return this.shopsService.getAllShops().pipe(
      tap((res) => {
        const singleShop = (res?.filter((el) => el.userHasAccess) ?? []).length === 1
        ctx.patchState({
          allShops: res,
          singleShopUser: singleShop,
          singleShopId: singleShop ? res?.find((el) => el.userHasAccess)?.id : undefined
        })
      })
    )
  }

  @Action(GetStatusBoard)
  getStatusBoard(ctx: StateContext<ShopsStateModel>) {
    ctx.patchState({
      statusBoardLocations: undefined
    })
    return this.shopsService.getStatusBoard().pipe(
      tap((res) => {
        ctx.patchState({ statusBoardLocations: res?.locations ?? [] })
      })
    )
  }

  @Action(GetAttentionSummary)
  getAttentionSummary(ctx: StateContext<ShopsStateModel>) {
    ctx.patchState({
      attentionSummary: undefined
    })
    return this.shopsService.getAttentionSummary().pipe(
      tap((res) => {
        ctx.patchState({ attentionSummary: res })
      })
    )
  }

  @Action(EditLocationAttention)
  editLocationAttention(
    ctx: StateContext<ShopsStateModel>,
    { locationId, payload }: EditLocationAttention
  ) {
    return this.shopsService.editLocationAttention(locationId, payload)
  }

  @Action(GetAllVendors)
  getAllVendors(ctx: StateContext<ShopsStateModel>, action: GetAllVendors) {
    ctx.patchState({
      allVendors: undefined
    })
    return this.shopsService.getAllVendors(action.payload).pipe(
      tap((res) => {
        ctx.patchState({ allVendors: res?.locations ?? [] })
      })
    )
  }

  @Action(GetVendorsSummaryByLevel)
  getVendorsSummaryByLevel(ctx: StateContext<ShopsStateModel>, action: GetVendorsSummaryByLevel) {
    ctx.patchState({
      vendorsSummaryByLevel: undefined
    })
    return this.shopsService.getVendorsSummaryByLevel(action.payload).pipe(
      tap((res) => {
        ctx.patchState({ vendorsSummaryByLevel: res?.locations ?? [] })
      })
    )
  }

  @Action(GetPreferredVendors)
  getPreferredVendors(ctx: StateContext<ShopsStateModel>) {
    ctx.patchState({
      preferredVendors: undefined
    })
    return this.shopsService.getPreferredVendors().pipe(
      tap((res) => {
        ctx.patchState({ preferredVendors: res?.locations ?? [] })
      })
    )
  }

  @Action(GetPreferredVendorsDetail)
  getPreferredVendorsDetail(
    ctx: StateContext<ShopsStateModel>,
    { locationId }: GetPreferredVendorsDetail
  ) {
    ctx.patchState({
      preferredVendorsDetail: undefined
    })
    return this.shopsService.getPreferredVendorsDetail(locationId).pipe(
      tap((res) => {
        ctx.patchState({ preferredVendorsDetail: res })
      })
    )
  }

  @Action(UpdatePreferredVendors)
  updatePreferredVendors(
    ctx: StateContext<ShopsStateModel>,
    { locationId, payload }: UpdatePreferredVendors
  ) {
    return this.shopsService.updatePreferredVendors(locationId, payload)
  }

  @Action(CopyPreferredVendors)
  copyPreferredVendors(
    ctx: StateContext<ShopsStateModel>,
    { locationId, payload }: CopyPreferredVendors
  ) {
    return this.shopsService.copyPreferredVendors(locationId, payload)
  }

  @Action(GetVendorsList)
  getVendorsList(ctx: StateContext<ShopsStateModel>, { info }: GetVendorsList) {
    ctx.patchState({
      vendorsList: undefined
    })
    return this.shopsService.getVendorsList(info).pipe(
      tap((res) => {
        ctx.patchState({ vendorsList: res })
      })
    )
  }

  @Action(GetVendorDocument)
  getVendorDocument(ctx: StateContext<ShopsStateModel>, { vendorDocumentId }: GetVendorDocument) {
    ctx.patchState({ vendorDocument: undefined })
    return this.shopsService.getVendorDocument(vendorDocumentId).pipe(
      tap((res) => {
        ctx.patchState({ vendorDocument: res })
      })
    )
  }

  @Action(GetAllRegionalManagers)
  getAllRegionalManagers(ctx: StateContext<ShopsStateModel>) {
    const { regionalManagers } = ctx.getState()
    if (regionalManagers && regionalManagers.length > 0) {
      return regionalManagers
    }
    return this.shopsService.getAllRegionalManagers().pipe(
      tap((res) => {
        ctx.patchState({ regionalManagers: res?.regionalManagers ?? [] })
      })
    )
  }

  @Action(GetAllDistrictManagers)
  getAllDistrictManagers(ctx: StateContext<ShopsStateModel>) {
    const { districtManagers } = ctx.getState()
    if (districtManagers && districtManagers.length > 0) {
      return districtManagers
    }
    return this.shopsService.getAllDistrictManagers().pipe(
      tap((res) => {
        ctx.patchState({ districtManagers: res?.districtManagers ?? [] })
      })
    )
  }

  @Action(UpdateLocationUsafWarehouse)
  updateLocationUsafWarehouse(
    ctx: StateContext<ShopsStateModel>,
    { locationId, usafWarehouse }: UpdateLocationUsafWarehouse
  ) {
    const allShops = ctx.getState().allShops ?? []
    const idx = allShops.findIndex((s) => s.id === locationId)
    if (idx < 0 || allShops[idx].usafWarehouse === usafWarehouse) return
    const next = [...allShops]
    next[idx] = { ...next[idx], usafWarehouse }
    ctx.patchState({ allShops: next })
  }

  @Action(GetAllLocationGroups)
  getAllLocationGroups(ctx: StateContext<ShopsStateModel>) {
    const { locationGroups } = ctx.getState()
    if (locationGroups && locationGroups.length > 0) {
      return locationGroups
    }
    return this.shopsService.getAllLocationGroups().pipe(
      tap((res) => {
        ctx.patchState({ locationGroups: res?.locationGroups ?? [] })
      })
    )
  }
}
